// SharePlaceDialog - Share a private place with other users

import React, { useState, useEffect, useCallback } from 'react';
import {
  X,
  Search,
  User,
  Loader2,
  UserMinus,
  UserPlus,
  Check,
  MapPin,
} from 'lucide-react';
import type { Place, PublicUserProfile } from '../../types';
import { searchUsers, getUserProfile } from '../../services/userSearchService';
import { sharePlaceWithUser, unsharePlaceWithUser } from '../../services/placesService';

interface SharePlaceDialogProps {
  place: Place;
  currentUserId: string;
  onClose: () => void;
  onUpdate: (updatedPlace: Place) => void;
  showNotification?: (message: string, type: 'success' | 'info' | 'error') => void;
}

export function SharePlaceDialog({
  place,
  currentUserId,
  onClose,
  onUpdate,
  showNotification,
}: SharePlaceDialogProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<PublicUserProfile[]>([]);
  const [sharedUsers, setSharedUsers] = useState<PublicUserProfile[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isLoadingShared, setIsLoadingShared] = useState(true);
  const [actionInProgress, setActionInProgress] = useState<string | null>(null);

  // Load shared users on mount
  useEffect(() => {
    const loadSharedUsers = async () => {
      if (!place.sharedWith || place.sharedWith.length === 0) {
        setIsLoadingShared(false);
        return;
      }

      try {
        const profiles = await Promise.all(
          place.sharedWith.map((userId) => getUserProfile(userId))
        );
        setSharedUsers(profiles.filter(Boolean) as PublicUserProfile[]);
      } catch (error) {
        console.error('Error loading shared users:', error);
      } finally {
        setIsLoadingShared(false);
      }
    };

    loadSharedUsers();
  }, [place.sharedWith]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(async () => {
      if (searchQuery.length < 2) {
        setSearchResults([]);
        return;
      }

      setIsSearching(true);
      try {
        const results = await searchUsers(searchQuery, 10);
        // Filter out current user and already shared users
        const filtered = results.filter(
          (user) =>
            user.id !== currentUserId &&
            !place.sharedWith?.includes(user.id)
        );
        setSearchResults(filtered);
      } catch (error) {
        console.error('Error searching users:', error);
      } finally {
        setIsSearching(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery, currentUserId, place.sharedWith]);

  // Share with user
  const handleShare = useCallback(
    async (user: PublicUserProfile) => {
      if (actionInProgress) return;

      setActionInProgress(user.id);
      try {
        await sharePlaceWithUser(place.id, currentUserId, user.id);

        // Update local state
        const updatedSharedWith = [...(place.sharedWith || []), user.id];
        const updatedPlace = { ...place, sharedWith: updatedSharedWith };
        onUpdate(updatedPlace);

        setSharedUsers((prev) => [...prev, user]);
        setSearchResults((prev) => prev.filter((u) => u.id !== user.id));
        setSearchQuery('');

        showNotification?.(`Mit ${user.displayName} geteilt`, 'success');
      } catch (error) {
        console.error('Error sharing place:', error);
        showNotification?.(
          error instanceof Error ? error.message : 'Fehler beim Teilen',
          'error'
        );
      } finally {
        setActionInProgress(null);
      }
    },
    [place, currentUserId, actionInProgress, onUpdate, showNotification]
  );

  // Unshare with user
  const handleUnshare = useCallback(
    async (user: PublicUserProfile) => {
      if (actionInProgress) return;

      setActionInProgress(user.id);
      try {
        await unsharePlaceWithUser(place.id, currentUserId, user.id);

        // Update local state
        const updatedSharedWith = (place.sharedWith || []).filter(
          (id) => id !== user.id
        );
        const updatedPlace = { ...place, sharedWith: updatedSharedWith };
        onUpdate(updatedPlace);

        setSharedUsers((prev) => prev.filter((u) => u.id !== user.id));

        showNotification?.(`Freigabe fuer ${user.displayName} entfernt`, 'info');
      } catch (error) {
        console.error('Error unsharing place:', error);
        showNotification?.(
          error instanceof Error ? error.message : 'Fehler beim Entfernen',
          'error'
        );
      } finally {
        setActionInProgress(null);
      }
    },
    [place, currentUserId, actionInProgress, onUpdate, showNotification]
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-2xl w-full max-w-md max-h-[80vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-indigo-100 rounded-lg">
              <MapPin className="w-5 h-5 text-indigo-600" />
            </div>
            <div>
              <h2 className="font-semibold text-gray-900">Ort teilen</h2>
              <p className="text-sm text-gray-500 truncate max-w-[200px]">
                {place.name}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Search */}
        <div className="p-4 border-b border-gray-200">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Name eingeben..."
              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none"
            />
            {isSearching && (
              <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-indigo-500 animate-spin" />
            )}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {/* Search Results */}
          {searchQuery.length >= 2 && (
            <div className="p-4">
              <h3 className="text-sm font-medium text-gray-500 mb-3">
                Suchergebnisse
              </h3>
              {isSearching ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 text-indigo-500 animate-spin" />
                </div>
              ) : searchResults.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-4">
                  Keine Nutzer gefunden
                </p>
              ) : (
                <div className="space-y-2">
                  {searchResults.map((user) => (
                    <div
                      key={user.id}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        {user.photoURL ? (
                          <img
                            src={user.photoURL}
                            alt={user.displayName}
                            className="w-10 h-10 rounded-full object-cover"
                          />
                        ) : (
                          <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center">
                            <User className="w-5 h-5 text-gray-400" />
                          </div>
                        )}
                        <span className="font-medium text-gray-900">
                          {user.displayName}
                        </span>
                      </div>
                      <button
                        onClick={() => handleShare(user)}
                        disabled={actionInProgress === user.id}
                        className="flex items-center gap-1 px-3 py-1.5 bg-indigo-500 text-white text-sm font-medium rounded-lg hover:bg-indigo-600 transition disabled:opacity-50"
                      >
                        {actionInProgress === user.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <>
                            <UserPlus className="w-4 h-4" />
                            Teilen
                          </>
                        )}
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Already Shared */}
          <div className="p-4">
            <h3 className="text-sm font-medium text-gray-500 mb-3">
              Bereits geteilt mit ({sharedUsers.length})
            </h3>
            {isLoadingShared ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 text-indigo-500 animate-spin" />
              </div>
            ) : sharedUsers.length === 0 ? (
              <div className="text-center py-8">
                <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <User className="w-6 h-6 text-gray-400" />
                </div>
                <p className="text-sm text-gray-500">
                  Noch mit niemandem geteilt
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                {sharedUsers.map((user) => (
                  <div
                    key={user.id}
                    className="flex items-center justify-between p-3 bg-green-50 rounded-lg border border-green-100"
                  >
                    <div className="flex items-center gap-3">
                      {user.photoURL ? (
                        <img
                          src={user.photoURL}
                          alt={user.displayName}
                          className="w-10 h-10 rounded-full object-cover"
                        />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                          <User className="w-5 h-5 text-green-600" />
                        </div>
                      )}
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900">
                          {user.displayName}
                        </span>
                        <Check className="w-4 h-4 text-green-600" />
                      </div>
                    </div>
                    <button
                      onClick={() => handleUnshare(user)}
                      disabled={actionInProgress === user.id}
                      className="flex items-center gap-1 px-3 py-1.5 bg-red-100 text-red-700 text-sm font-medium rounded-lg hover:bg-red-200 transition disabled:opacity-50"
                    >
                      {actionInProgress === user.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          <UserMinus className="w-4 h-4" />
                          Entfernen
                        </>
                      )}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200">
          <button
            onClick={onClose}
            className="w-full py-3 bg-gray-100 text-gray-700 font-semibold rounded-lg hover:bg-gray-200 transition"
          >
            Fertig
          </button>
        </div>
      </div>
    </div>
  );
}

export default SharePlaceDialog;
